import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PlayCircle, FileText, Menu, X } from "lucide-react";
import { Brand } from "@/components/Brand";
import { NodeStatusPill } from "@/components/NodeStatusPill";

const NAV = [
    { label: "Plain English", to: "/explained" },
    { label: "Demo film", href: "/katlas_swift_demo.mp4?v=2", icon: PlayCircle },
    {
        label: "Proposal",
        href: "/iso20022_coverage_and_gap_proposal.pdf",
        icon: FileText,
    },
    { label: "Enforcement", to: "/enforcement" },
    { label: "Live engine", to: "/live" },
    { label: "Bond transfer", to: "/request/TX-BOND-001" },
    { label: "Freeze review", to: "/request/TX-BOND-002" },
    { label: "Corporate action", to: "/request/CA-ELEC-001" },
    { label: "Standards change", to: "/standards-change" },
    { label: "Structured data", to: "/standards" },
];

export const Layout = ({ children, wide = false }) => {
    const { pathname } = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    return (
        <div className="flex min-h-screen flex-col bg-slate-50">
            <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
                    <Brand />
                    <button
                        type="button"
                        onClick={() => setMenuOpen((o) => !o)}
                        data-testid="nav-menu-toggle"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        className="rounded-sm p-2 text-slate-700 hover:bg-slate-50 md:hidden"
                    >
                        {menuOpen ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
                    </button>
                    <nav className="hidden items-center gap-1 md:flex">
                        {NAV.map((n) =>
                            n.href ? (
                                <a
                                    key={n.href}
                                    href={n.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                                    className="inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm text-blue-800 transition-colors duration-150 hover:bg-blue-50 hover:text-blue-950"
                                >
                                    <n.icon className="h-4 w-4" strokeWidth={1.5} />
                                    {n.label}
                                </a>
                            ) : (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    data-testid={`nav-${n.to.replace(/\//g, "-")}`}
                                    className={`rounded-sm px-3 py-1.5 text-sm transition-colors duration-150 ${
                                        pathname === n.to
                                            ? "bg-slate-100 text-slate-900"
                                            : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                                    }`}
                                >
                                    {n.label}
                                </Link>
                            )
                        )}
                    </nav>
                </div>
                {menuOpen && (
                    <nav
                        data-testid="nav-mobile"
                        className="border-t border-slate-200 bg-white px-4 pb-3 pt-2 md:hidden"
                    >
                        {NAV.map((n) =>
                            n.href ? (
                                <a
                                    key={n.href}
                                    href={n.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setMenuOpen(false)}
                                    className="flex items-center gap-2 rounded-sm px-3 py-2.5 text-sm text-blue-800 hover:bg-blue-50"
                                >
                                    <n.icon className="h-4 w-4" strokeWidth={1.5} />
                                    {n.label}
                                </a>
                            ) : (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    onClick={() => setMenuOpen(false)}
                                    className={`block rounded-sm px-3 py-2.5 text-sm ${
                                        pathname === n.to
                                            ? "bg-slate-100 text-slate-900"
                                            : "text-slate-600 hover:bg-slate-50"
                                    }`}
                                >
                                    {n.label}
                                </Link>
                            )
                        )}
                    </nav>
                )}
            </header>

            <main className={`mx-auto w-full flex-1 px-6 py-10 ${wide ? "max-w-6xl" : "max-w-6xl"}`}>
                {children}
            </main>

            <footer className="border-t border-slate-200 bg-white">
                <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-6 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-xl leading-relaxed">
                        Synthetic standards-oriented sandbox. No real financial assets,
                        custody, settlement or customer data. Not a regulatory or
                        compliance certification.
                    </p>
                    <div className="flex items-center gap-4">
                        <span data-testid="build-stamp" className="font-mono text-slate-300">
                            {process.env.REACT_APP_BUILD || "dev"}
                        </span>
                        <Link
                            to="/diagnostics"
                            data-testid="footer-diagnostics-link"
                            className="font-mono text-slate-400 hover:text-slate-700"
                        >
                            Diagnostics
                        </Link>
                        <NodeStatusPill />
                    </div>
                </div>
            </footer>
        </div>
    );
};
